<template>
  <div class="m-2 text-left">
    <h1 class="mainheader p-2">TAIR BLAST Results</h1>
    <p class="pl-2">
      <router-link :to="{ name: 'Blast' }">New BLAST search</router-link>
    </p>

    <div class="sr-only" role="status" aria-live="polite">
      {{ statusMessage }}
    </div>

    <div v-if="pending" class="pl-2">
      <p>
        <span
          class="spinner-border spinner-border-sm mr-2"
          aria-hidden="true"
        ></span>
        <b>{{ job && job.status === "running" ? "Running" : "Queued" }}</b>
        <span v-if="job"> ({{ job.elapsedSeconds }} s)</span>
      </p>
      <p>
        Your BLAST search is being processed. Please keep this page open; it
        updates by itself. Searches against large datasets with TBLASTN or
        TBLASTX can take several minutes.
      </p>
    </div>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      <div style="white-space: pre-line">{{ errorMessage }}</div>
      <router-link :to="{ name: 'Blast' }">Back to the BLAST form</router-link>
    </div>

    <div v-if="result" class="pl-2">
      <p class="mb-1">
        <b
          >{{ result.algorithm }} query on
          {{ result.dataset.label }} sequences</b
        >
      </p>
      <p v-if="result.title" class="mb-1">
        <b>Results for</b> <i>{{ result.title }}</i>
      </p>

      <div
        v-if="result.notFound.length"
        class="alert alert-warning"
        role="alert"
      >
        The following loci have no sequences found:
        {{ result.notFound.join(", ") }}
      </div>
      <div v-if="result.warnings.length" class="alert alert-warning">
        BLAST job completed with the following warnings:
        <ul class="mb-0">
          <li v-for="(w, i) in result.warnings" :key="i">{{ w }}</li>
        </ul>
      </div>
      <p v-if="result.skippedOptions.length">
        <i>The following options</i>
        <b>{{ result.skippedOptions.join(", ") }}</b>
        <i>
          were skipped and not used in your run because they are not applicable
          to the algorithm</i
        >
        <b>{{ result.algorithm }}</b> <i>that you selected.</i>
      </p>

      <p>
        <i
          >Query performed by
          <a href="https://www.arabidopsis.org/">
            The Arabidopsis Information Resource (TAIR)</a
          >; for full BLAST options and parameters, refer to the NCBI
          <a
            href="https://blast.ncbi.nlm.nih.gov/doc/blast-help/"
            target="_blank"
            >BLAST Documentation</a
          ></i
        >
      </p>
      <p>
        <button
          type="button"
          class="btn btn-secondary btn-sm"
          @click="downloadText"
        >
          Download results as text
        </button>
      </p>

      <BlastHitGraphic
        v-for="(query, i) in result.graphics"
        :key="i"
        :query="query"
        @select="scrollToAnchor"
      />

      <pre
        ref="report"
        class="blast-report border rounded p-2"
        @click="onReportClick"
        v-html="result.html"
      ></pre>
    </div>
  </div>
</template>

<script>
import PostsService from "@/services/PostsService";
import BlastHitGraphic from "@/components/tools/BlastHitGraphic.vue";
import { classifyPollError, nextPollDelay } from "@/services/toolJobPolling";

export default {
  name: "BlastResults",
  components: { BlastHitGraphic },
  data: () => ({
    job: null,
    result: null,
    errorMessage: "",
    pollTimer: null,
    // Bumped by stopPolling so a request already in flight is ignored.
    pollGeneration: 0,
    pollStartedAt: Date.now(),
  }),
  computed: {
    pending() {
      return !this.result && !this.errorMessage;
    },
    statusMessage() {
      if (this.result) return "BLAST search finished.";
      if (this.errorMessage) return "BLAST search failed.";
      return "BLAST search in progress.";
    },
  },
  watch: {
    "$route.params.jobId"() {
      this.stopPolling();
      this.pollStartedAt = Date.now();
      Object.assign(this, { job: null, result: null, errorMessage: "" });
      this.poll();
    },
  },
  created() {
    this.poll();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    stopPolling() {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.pollGeneration++;
    },
    async poll() {
      const generation = this.pollGeneration;
      let response = null;
      let failure = null;
      try {
        response = await PostsService.getBlastJob(this.$route.params.jobId);
      } catch (err) {
        failure = classifyPollError(err, "BLAST");
      }
      // The page was left or switched to another job while waiting.
      if (generation !== this.pollGeneration) return;

      if (failure) {
        if (!failure.retry) {
          this.errorMessage = failure.message;
          return;
        }
      } else {
        const { data } = response;
        this.job = data;
        if (data.status === "done") {
          this.result = data.result;
          return;
        }
        if (data.status === "error") {
          this.errorMessage = data.error || "The BLAST search failed.";
          return;
        }
      }
      this.pollTimer = setTimeout(this.poll, nextPollDelay(this.pollStartedAt));
    },
    onReportClick(event) {
      const anchor = event.target.closest("a.blast-anchor");
      if (!anchor) return;
      event.preventDefault();
      this.scrollToAnchor(anchor.getAttribute("href").slice(1));
    },
    scrollToAnchor(id) {
      const el = this.$refs.report && this.$refs.report.querySelector(`#${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    downloadText() {
      const blob = new Blob([this.result.text], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `TAIR_BLAST_${
        this.result.algorithm
      }_${this.$route.params.jobId.slice(0, 8)}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.mainheader {
  font-size: 16pt;
  font-weight: bold;
  color: #930;
}
.blast-report {
  font-size: 12px;
  background: #fff;
  white-space: pre;
  overflow-x: auto;
}
</style>
